import ContentLayout from '@cloudscape-design/components/content-layout';
import {
  Box,
  Button,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppPagination, { AppPaginationRef } from 'src/components/AppPagination';
import { PaginationConfig } from 'src/contstant/PaginationConfig';

import {
  defaultVisibleColumns,
  exportAuditsConfig,
  getAudiSummaryUrl,
  getAuditsTableDefinition,
  getPerformAuditUrl,
} from './auditsConfig';
import { ExportButton } from 'src/components/ExportButton';
import { useAudits } from 'src/store/audits/useAudits';
import useUserProfile from 'src/hooks/useUserProfile';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import TablePreferences, { TablePreferencesRef } from 'src/components/TablePreferences';
import { Audit, SearchAuditsRequest } from 'src/types/helios/Audit';
import AuditFilterView from 'src/components/audit/AuditFilterView';
import CreateAuditModal from 'src/components/audit/CreateAuditModal';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { AuditStatus, PAGE_SIZE_OPTIONS } from 'src/contstant/commonLabels';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { ApiRequestStatus } from 'src/types/ApiRequestStatus';
import { usePageFilters } from 'src/hooks/usePageFilters';
import { countActiveFilters } from 'src/utils/filterPersistence';
import { getPageFilterKey } from 'src/contstant/storageKeys';
import { useUserDetails } from 'src/providers/UserProvider';
import { Authorized } from 'src/components/Authorized';
import { ShareFiltersButton } from 'src/components/ShareFiltersButton';
import { BusinessUnitMismatchHandler } from 'src/components/BusinessUnitMismatchHandler';
import { SharedModeBanner } from 'src/components/SharedModeBanner';

const AUDIT_CREATION_MESSAGE_TIMEOUT = 20000;

// Default audit statuses for search - excludes CANCELLED and SCHEDULED to avoid cluttering the view
const DEFAULT_AUDIT_STATUSES = [
  AuditStatus.NOT_STARTED,
  AuditStatus.IN_PROGRESS,
  AuditStatus.COMPLETED,
  AuditStatus.EXPIRED,
];

/**
 * AuditsPage - Main page for viewing and managing audits
 * Displays audits in a table with filtering, pagination, and CRUD operations
 */
const AuditsPage = () => {
  // Navigation and refs
  const navigate = useNavigate();
  const useRefPagiantion = useRef<AppPaginationRef>(null);
  const tablePreferencesRef = useRef<TablePreferencesRef>(null);
  const [validationStatus, setValidationStatus] = useState<ApiRequestStatus[]>([]);
  // Custom hooks for data management
  const { selectCurrentAudit, createAuditApiStatus } = useAudits();
  const {
    auditsList,
    searchAudits,
    searchAuditsApiStatus,
    updateAudit,
    clearStatusMessages,
    resetApiStatus,
  } = useAudits();

  const { getUserId, getUserBusinessUnitId, currentUser, getLocations } =
    useUserProfile();
  const { setContent, setTitle, closeView, content, setPanelSize } = useSplitPanel();

  // Get business unit ID for scoped filter persistence
  const businessUnitId = getUserBusinessUnitId();

  // Memoize default filters with stable reference
  const defaultFilters = useMemo(
    (): SearchAuditsRequest => ({
      businessUnitId: getUserBusinessUnitId(),
      locations: getLocations(),
      statuses: DEFAULT_AUDIT_STATUSES,
      // Explicit undefined for all clearable fields
      templateIds: undefined,
      auditTypes: undefined,
      divisionName: undefined,
      assignees: undefined,
      dateRange: undefined,
      keyword: undefined,
    }),
    [getUserBusinessUnitId, getLocations],
  );

  // Use page filters hook (handles both shared and persisted modes)
  const {
    filters: searchParams,
    setFilters: setSearchParams,
    clearFilters,
    resetTarget,
    isSharedMode,
    isBUMismatch,
  } = usePageFilters({
    storageKey: getPageFilterKey(businessUnitId, 'audits'),
    defaultFilters,
    excludeFields: ['businessUnitId', 'maxResults', 'nextToken'],
    universalFields: ['locations', 'divisionName'],
    currentBusinessUnitId: businessUnitId,
  });

  // Count active filters using generic utility
  const activeFilterCount = useMemo(() => {
    return countActiveFilters(searchParams, [
      'businessUnitId',
      'maxResults',
      'nextToken',
    ]);
  }, [searchParams]);

  const [createModalVisible, setCreateModalVisible] = useState(false); // Create audit modal visibility
  const [shareFilterStatus, setShareFilterStatus] = useState<ApiRequestStatus[]>([]); // Share filter status messages

  // Navigation functions
  const viewAudit = (item: Audit) => {
    navigate(getAudiSummaryUrl(item));
  };
  const performAudit = (item: Audit) => {
    navigate(getPerformAuditUrl(item));
  };

  // Audit action functions
  const cancelAudit = (item: Audit) => {
    updateAudit({
      auditId: item.auditId,
      status: AuditStatus.CANCELLED,
      businessUnitId: item.businessUnit,
      lastUpdatedBy: getUserId(),
    });
  };
  const { permissions } = useUserDetails();

  const permissionMap = useMemo(() => {
    return {
      edit: {
        situation: permissions.has('EditSituationAudit'),
        progress: permissions.has('EditProgressAudit'),
        calibration: permissions.has('EditCalibrationAudit'),
        mpa: permissions.has('EditMpaAudit'),
        test: permissions.has('EditTestAudit'),
      },
      perform: {
        situation: permissions.has('PerformSituationAudit'),
        progress: permissions.has('PerformProgressAudit'),
        calibration: permissions.has('PerformCalibrationAudit'),
        mpa: permissions.has('PerformMpaAudit'),
        test: permissions.has('PerformTestAudit'),
      },
    } as const;
  }, [permissions]);
  // Define column preferences for table customization
  const allColumnDefinition = useMemo(() => {
    return getAuditsTableDefinition(
      viewAudit,
      performAudit,
      cancelAudit,
      permissionMap,
    ).filter((col) => col.id !== undefined);
  }, [viewAudit, performAudit, cancelAudit, permissionMap]);

  // Table preferences state - manages visible columns and pagination
  const [tablePrefs, setTablePrefs] = useState<{
    visibleColumns: any[];
    paginatedItems: Audit[];
    currentPage: number;
    pageSize: number;
  }>({
    visibleColumns: [],
    paginatedItems: [],
    currentPage: 1,
    pageSize: 0,
  });

  /**
   * Handles changes to table preferences (columns, pagination)
   */
  const handlePreferencesChange = (data: {
    visibleColumns: any[];
    paginatedItems: Audit[];
    currentPage: number;
    pageSize: number;
  }) => {
    setTablePrefs((prev) => ({
      ...prev,
      visibleColumns: data.visibleColumns,
      currentPage: data.currentPage,
      pageSize: data.pageSize,
      paginatedItems: data.paginatedItems,
    }));
  };

  // Reload data when user or search parameters change
  useEffect(() => {
    // Don't load data if there's a BU mismatch
    if (isBUMismatch) {
      return;
    }
    if (searchParams) {
      loadData(undefined, tablePrefs.pageSize);
    }
  }, [currentUser, searchParams, tablePrefs.pageSize, isBUMismatch]);

  // Close the split panel when the component unmounts
  useEffect(() => {
    return () => {
      closeView();
    };
  }, []);

  useEffect(() => {
    if (createAuditApiStatus.isSuccess) {
      setValidationStatus([
        {
          isLoading: false,
          isSuccess: true,
          successMessage:
            'Audit creation in progress. Your audit will be ready in approximately 1 minute.',
          errorMessage: null,
        },
      ]);
      const timeoutId = setTimeout(() => {
        setValidationStatus([]);
      }, AUDIT_CREATION_MESSAGE_TIMEOUT);
      return () => clearTimeout(timeoutId);
    }
  }, [createAuditApiStatus.isSuccess]);

  //when changing the language from dropdown , it refresh the content
  useEffect(() => {
    tablePreferencesRef.current?.refreshColumns();
  }, [permissions]);

  /**
   * Loads audits data with pagination and search parameters
   */
  const loadData = (nextToken?: string, maxResults?: number) => {
    if (maxResults === 0) {
      return;
    }
    if (!nextToken) {
      useRefPagiantion?.current?.clearTokens();
    }
    searchAudits({
      ...searchParams,
      maxResults: maxResults,
      nextToken: nextToken,
      businessUnitId: getUserBusinessUnitId(),
    });
  };

  // Table header with action buttons
  const tableHeader = (
    <Header
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          {/* Refresh data button */}
          <Button
            data-testid="refresh-button"
            iconName="refresh"
            onClick={() => loadData(undefined, tablePrefs.pageSize)}
          />
          {/* Export audits button */}
          <ExportButton
            data-testid="export-button"
            data={auditsList}
            config={exportAuditsConfig}
            onError={(error) => {
              alert(error);
            }}
          />
          {/* Create new audit button */}
          <Authorized
            permissionsToCheck={[
              'EditSituationAudit',
              'EditProgressAudit',
              'EditCalibrationAudit',
              'EditMpaAudit',
              'EditTestAudit',
            ]}
            method="ANY"
          >
            <Button
              variant="normal"
              data-testid="create-audit-button"
              onClick={() => {
                selectCurrentAudit();
                setCreateModalVisible(true);
              }}
            >
              Create Audit
            </Button>
          </Authorized>

          {/* Create audit modal */}
          <CreateAuditModal
            data-testid="create-audit-modal"
            visible={createModalVisible}
            isEditCalibrationAllow={permissionMap.edit.calibration}
            isEditProgressAllow={permissionMap.edit.progress}
            isEditSituationAllow={permissionMap.edit.situation}
            isEditMpaAllow={permissionMap.edit.mpa}
            isEditTestAudit={permissionMap.edit.test}
            onDismiss={() => setCreateModalVisible(false)}
          />
          {/* Share filters button - always shown (except during BU mismatch) */}
          {!isBUMismatch && (
            <ShareFiltersButton
              data-testid="share-filters-button"
              filters={searchParams}
              businessUnitId={businessUnitId}
              onStatusChange={setShareFilterStatus}
            />
          )}
          {/* Filter button - only shown when split panel is closed */}
          {!content && !isBUMismatch && (
            <Button
              data-testid="filter-button"
              iconName="filter"
              onClick={() => {
                openFilterView();
              }}
            >
              {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : 'Filter'}
            </Button>
          )}
        </SpaceBetween>
      }
    >
      Audits
    </Header>
  );

  /**
   * Opens the split panel with audit filter options
   */
  const openFilterView = () => {
    setPanelSize(330);
    setTitle('Filter');
    setContent(
      <AuditFilterView
        key="audit-filter-view"
        data-testid="audit-filter-view"
        defaultSearchParams={searchParams}
        resetSearchParams={resetTarget}
        onSearchChange={setSearchParams}
        onClearAll={clearFilters}
      />,
    );
  };

  // Update split panel content when searchParams changes (for cross-tab sync)
  useEffect(() => {
    if (
      content &&
      typeof content === 'object' &&
      'type' in content &&
      content.type === AuditFilterView
    ) {
      setContent(
        <AuditFilterView
          key="audit-filter-view"
          data-testid="audit-filter-view"
          defaultSearchParams={searchParams}
          resetSearchParams={resetTarget}
          onSearchChange={setSearchParams}
          onClearAll={clearFilters}
        />,
      );
    }
  }, [searchParams]);

  return (
    <ContentLayout data-testid="audits-content-layout">
      <Box data-testid="audits-container" margin={{ top: 's' }}>
        {/* Shared mode banner - shown when viewing a shared URL */}
        <SharedModeBanner isSharedMode={isSharedMode} isBUMismatch={isBUMismatch} />
        <ApiStatusFlashBar
          data-testid="api-status-flashbar"
          apiStatus={[...validationStatus, ...shareFilterStatus, searchAuditsApiStatus]}
          autoDismissDelay={
            validationStatus.length > 0 ? AUDIT_CREATION_MESSAGE_TIMEOUT : undefined
          }
          clearMessages={() => {
            clearStatusMessages();
            resetApiStatus();
            setValidationStatus([]);
            setShareFilterStatus([]);
          }}
        />
        {/* Main audits table with full functionality */}
        <Table
          data-testid="audits-table"
          columnDefinitions={tablePrefs.visibleColumns}
          items={auditsList}
          loading={searchAuditsApiStatus.isLoading}
          loadingText="Loading Audits..."
          header={tableHeader}
          filter={
            <SpaceBetween
              data-testid="filter-space-between"
              size="m"
              direction="horizontal"
            >
              {/* Keyword search filter */}
              <TextFilter
                data-testid="keyword-search-filter"
                filteringText={searchParams?.keyword || ''}
                filteringPlaceholder="Search Audits"
                filteringAriaLabel="Search Audits"
                onChange={({ detail }) => {
                  setSearchParams((prev) => ({
                    ...prev!,
                    keyword:
                      detail.filteringText === '' ? undefined : detail.filteringText,
                  }));
                }}
              />
            </SpaceBetween>
          }
          pagination={
            <AppPagination
              data-testid="audits-pagination"
              ref={useRefPagiantion}
              nextToken={searchAuditsApiStatus.nextToken}
              onChange={(nextToken?: string) => {
                loadData(nextToken, tablePrefs.pageSize);
              }}
            />
          }
          preferences={
            <TablePreferences
              tableId="audits-table"
              ref={tablePreferencesRef}
              defaultVisibleColumns={defaultVisibleColumns}
              defaultPageSize={PaginationConfig.AUDIT_MAX_RESULT}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              allColumnDefinitions={allColumnDefinition}
              allItems={auditsList || []}
              onPreferencesChange={handlePreferencesChange}
            />
          }
          stripedRows
          stickyHeader
          empty={
            <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
              <SpaceBetween size="m">
                <StatusIndicator type="warning">No Audits.</StatusIndicator>
              </SpaceBetween>
            </Box>
          }
        />
      </Box>

      {/* Business Unit Mismatch Handler - automatically handles BU mismatch scenarios */}
      <BusinessUnitMismatchHandler
        isSharedMode={isSharedMode}
        isBUMismatch={isBUMismatch}
        currentBusinessUnitId={businessUnitId}
        requiredBusinessUnitId={searchParams?.businessUnitId}
        returnPath="/audits"
      />
    </ContentLayout>
  );
};

export default AuditsPage;
